// Réglages du joueur (son, image, affichage), gardés dans le navigateur. Panneau ouvert depuis la planque ou avec Échap en raid.

export interface Settings {
  master: number; // 0 à 100
  sfx: number;
  amb: number;
  music: number;
  ui: number;
  quality: 'basse' | 'moyenne' | 'haute';
  shadows: boolean;
  markers: boolean; // repères des meubles à fouiller
  fps: boolean;
  sens: number; // sensibilité de la souris (50 : normale)
  fov: number; // champ de vision (degrés, vertical)
}

const DEFAULTS: Settings = { master: 80, sfx: 100, amb: 70, music: 70, ui: 80, quality: 'haute', shadows: true, markers: true, fps: false, sens: 50, fov: 70 };
const KEY = 'zm-settings';

function load(): Settings {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') }; } catch { return { ...DEFAULTS }; }
}

export const settings: Settings = load();
type Listener = (s: Settings) => void;
const listeners: Listener[] = [];
export const onSettings = (l: Listener) => { listeners.push(l); l(settings); };

function save() {
  try { localStorage.setItem(KEY, JSON.stringify(settings)); } catch { /* indisponible */ }
  for (const l of listeners) l(settings);
}

const $ = (id: string) => document.getElementById(id)!;

// Remplit le panneau et branche chaque contrôle
export function initSettingsPanel() {
  const panel = $('settings');
  const slider = (id: keyof Settings, label: string, min = 0, max = 100, unit = '%') => `
    <label class="row"><span>${label}</span><input type="range" min="${min}" max="${max}" step="1" data-k="${id}" data-u="${unit}" value="${settings[id]}"><output>${settings[id]} ${unit}</output></label>`;
  const toggle = (id: keyof Settings, label: string, hint: string) => `
    <label class="row toggle"><span>${label}<small>${hint}</small></span><input type="checkbox" data-k="${id}" ${settings[id] ? 'checked' : ''}><i></i></label>`;
  $('setBody').innerHTML = `
    <section><h4>Son</h4>${slider('master', 'Volume général')}${slider('sfx', 'Effets (tirs, pas, infectés)')}${slider('amb', 'Ambiance (vent, grillons, tirs lointains)')}${slider('music', 'Musique des menus')}${slider('ui', 'Interface (clics, achats)')}</section>
    <section><h4>Image</h4>
      <label class="row"><span>Qualité<small>Basse : moins de détails, plus fluide sur un vieux PC</small></span>
        <select data-k="quality">${(['basse', 'moyenne', 'haute'] as const).map(q => `<option value="${q}" ${settings.quality === q ? 'selected' : ''}>${q[0]!.toUpperCase() + q.slice(1)}</option>`).join('')}</select></label>
      ${toggle('shadows', 'Ombres', 'Les désactiver soulage beaucoup la carte graphique')}
    </section>
    <section><h4>Contrôles</h4>${slider('sens', 'Sensibilité de la souris', 1, 100, '')}${slider('fov', 'Champ de vision', 55, 95, '°')}</section>
    <section><h4>Affichage</h4>
      ${toggle('markers', 'Repères de fouille', 'Nom des meubles à fouiller autour de toi')}
      ${toggle('fps', 'Images par seconde', 'Compteur en haut à droite')}
    </section>
    <section id="setAccount"></section>`;
  panel.oninput = e => {
    const t = e.target as HTMLInputElement | HTMLSelectElement;
    const k = t.dataset.k as keyof Settings | undefined;
    if (!k) return;
    if (t instanceof HTMLInputElement && t.type === 'checkbox') (settings[k] as boolean) = t.checked;
    else if (t instanceof HTMLInputElement && t.type === 'range') { (settings[k] as number) = Number(t.value); (t.nextElementSibling as HTMLOutputElement).textContent = `${t.value} ${t.dataset.u ?? '%'}`; }
    else (settings[k] as string) = t.value;
    save();
  };
  $('setClose').onclick = () => closeSettings();
  $('setReset').onclick = () => { Object.assign(settings, DEFAULTS); save(); initSettingsPanel(); };
  panel.onclick = e => { if (e.target === panel) closeSettings(); };
}

export const settingsOpen = () => !$('settings').classList.contains('hidden');
export function openSettings(inRaid: boolean) {
  $('setRaidNote').classList.toggle('hidden', !inRaid);
  renderAccount(inRaid);
  $('settings').classList.remove('hidden');
}

// ---------------------------------------------------------------- Compte

// Le compte est celui de Discord : se connecter suffit pour retrouver son profil partout (navigateur, launcher, autre PC).
// Sans Discord configuré (serveur de test sur son PC), on joue avec un profil invité propre à cet appareil.
export interface AccountInfo { discord: boolean; user: { name: string; avatar: string | null } | null }
let account: AccountInfo = { discord: false, user: null };
export const setAccount = (a: AccountInfo) => { account = a; };
const esc = (t: string) => t.replace(/[<>&"]/g, c => `&#${c.charCodeAt(0)};`);
function renderAccount(inRaid: boolean) {
  const u = account.user;
  $('setAccount').innerHTML = `<h4>Compte</h4>${u
    ? `<div class="acctWho">${u.avatar ? `<img src="${esc(u.avatar)}" alt="">` : ''}<span><small>Connecté avec Discord</small><b>${esc(u.name)}</b></span>
       ${inRaid ? '<em>Déconnexion hors raid</em>' : '<a class="ghostBtn" href="auth/logout">Se déconnecter</a>'}</div>
       <p class="hint">Ton inventaire, ta planque et ta progression sont enregistrés sur le serveur, liés à ton compte Discord.</p>`
    : `<p class="hint">${account.discord ? 'Profil invité sur cet appareil. Connecte-toi avec Discord depuis l’accueil pour garder ta progression partout.' : 'Profil invité sur cet appareil (serveur sans connexion Discord).'}</p>`}`;
}
export function closeSettings() { $('settings').classList.add('hidden'); }
